import { useEffect } from 'react';

import { DATA_TABLE_ROW_HEIGHT_MAP } from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';

const CLOSE_THRESHOLD = DATA_TABLE_ROW_HEIGHT_MAP.default;

/**
 * Closes the right-click floating menu once the user scrolls far enough. The menu is
 * portaled to the body at fixed coordinates, so it does not follow the table on scroll —
 * it is dismissed instead of being left floating detached from its anchor.
 *
 * When `stickyVertically` is true (header menus), vertical scroll of the table container
 * is ignored because the header is sticky and its position doesn't change vertically.
 * Page-level wheel/touch events and horizontal container scroll still close the menu.
 */
export function useCloseFloatingMenuOnScroll(props: {
  isMenuOpen: boolean;
  closeMenu: () => void;
  stickyVertically?: boolean;
}): void {
  const { isMenuOpen, closeMenu, stickyVertically = false } = props;
  const table = useDataTable();

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    // Only close once the accumulated scroll distance crosses this threshold. A small
    // threshold avoids closing the menu on minimal or unintentional scrolling.
    const closeThreshold = CLOSE_THRESHOLD;
    let scrolledDistance = 0;

    const accumulate = (delta: number) => {
      scrolledDistance += Math.abs(delta);
    };

    const closeIfScrolledFarEnough = () => {
      if (scrolledDistance >= closeThreshold) {
        closeMenu();
      }
    };

    const containerElement = table.getRowVirtualizationContainer();
    let lastContainerScrollTop = containerElement?.scrollTop ?? 0;
    let lastContainerScrollLeft = containerElement?.scrollLeft ?? 0;
    let lastTouchY: number | null = null;
    let lastTouchX: number | null = null;

    // For page-level scrolling we listen to user gestures (wheel/touch) rather than the
    // `scroll` event: opening the menu auto-focuses its first item, which can scroll the
    // document programmatically and would otherwise close the menu the instant it opens.
    const handleWheel = (event: WheelEvent) => {
      // For sticky-vertical anchors (e.g. column headers) we want to ignore vertical
      // wheel events that will scroll the table container — the header is sticky and
      // doesn't move. But if the container has no room to scroll in the wheel direction
      // (already at the boundary, or not overflow-scrollable at all), the event will
      // scroll the page instead, and the menu anchor's position is now wrong — close it.
      const containerWillScrollVertically =
        stickyVertically &&
        containerElement !== null &&
        containerElement !== undefined &&
        containerElement.contains(event.target as Node) &&
        (event.deltaY > 0
          ? containerElement.scrollTop + containerElement.clientHeight <
            containerElement.scrollHeight
          : containerElement.scrollTop > 0);

      accumulate(
        containerWillScrollVertically
          ? Math.abs(event.deltaX)
          : Math.max(Math.abs(event.deltaX), Math.abs(event.deltaY)),
      );
      closeIfScrolledFarEnough();
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      if (lastTouchY !== null && lastTouchX !== null) {
        accumulate(
          Math.max(
            Math.abs(touch.clientX - lastTouchX),
            Math.abs(touch.clientY - lastTouchY),
          ),
        );
      }
      lastTouchY = touch.clientY;
      lastTouchX = touch.clientX;
      closeIfScrolledFarEnough();
    };

    // The virtualization container's `scroll` event is safe to use directly because the
    // body-portaled menu never scrolls that container when it gains focus.
    const handleContainerScroll = () => {
      if (!containerElement) {
        return;
      }
      accumulate(
        stickyVertically
          ? Math.abs(containerElement.scrollLeft - lastContainerScrollLeft)
          : Math.max(
              Math.abs(containerElement.scrollTop - lastContainerScrollTop),
              Math.abs(containerElement.scrollLeft - lastContainerScrollLeft),
            ),
      );
      lastContainerScrollTop = containerElement.scrollTop;
      lastContainerScrollLeft = containerElement.scrollLeft;
      closeIfScrolledFarEnough();
    };

    window.addEventListener('wheel', handleWheel, {
      passive: true,
      capture: true,
    });
    window.addEventListener('touchmove', handleTouchMove, {
      passive: true,
      capture: true,
    });
    containerElement?.addEventListener('scroll', handleContainerScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('wheel', handleWheel, { capture: true });
      window.removeEventListener('touchmove', handleTouchMove, {
        capture: true,
      });
      containerElement?.removeEventListener('scroll', handleContainerScroll);
    };
  }, [isMenuOpen, closeMenu, stickyVertically, table]);
}
